import React, { Suspense, lazy, useEffect, useState } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import Theme from "@amzn/meridian/theme"
import { FacilityProvider } from "src/context/FacilityContext"
import { isAuthenticated, redirectToLogin, getStoredTokensAsync, scheduleTokenRefresh, stopTokenRefresh } from "src/services/authService"
import ErrorBoundary from "src/components/ErrorBoundary"
import RouteErrorBoundary from "src/components/RouteErrorBoundary"
import AppShell from "src/components/AppShell"
import Loader from "@amzn/meridian/loader"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import Heading from "@amzn/meridian/heading"
import Text from "@amzn/meridian/text"
import wfmTheme from "src/theme/wfm-theme.json"

// Lazy-loaded pages — reduces initial bundle by ~70%
const Dashboard = lazy(() => import("src/pages/Dashboard"))
const PODetail = lazy(() => import("src/pages/PODetail"))
const POCreate = lazy(() => import("src/pages/POCreate"))
const ReceiptDetail = lazy(() => import("src/pages/ReceiptDetail"))
const InvoiceDetail = lazy(() => import("src/pages/InvoiceDetail"))
const RecipeDetail = lazy(() => import("src/pages/RecipeDetail"))
const ItemDetail = lazy(() => import("src/pages/ItemDetail"))
const SupplierDetail = lazy(() => import("src/pages/SupplierDetail"))
const WorkOrderDetail = lazy(() => import("src/pages/WorkOrderDetail"))
const PickBatchDetail = lazy(() => import("src/pages/PickBatchDetail"))
const StoreOrderDetail = lazy(() => import("src/pages/StoreOrderDetail"))
const Callback = lazy(() => import("src/pages/Callback"))

// Section landing pages — each renders a Meridian TabGroup over its sub-pages.
// The sidebar links to the section root; the landing renders for the section
// root AND every sub-route so the tab bar stays visible (e.g. Create Work
// Order, Quality Hold, Cost Summary are tabs, not orphaned routes).
const PurchasingLanding = lazy(() => import("src/pages/PurchasingLanding"))
const ItemsLanding = lazy(() => import("src/pages/ItemsLanding"))
const InventoryLanding = lazy(() => import("src/pages/InventoryLanding"))
const ManufacturingLanding = lazy(() => import("src/pages/ManufacturingLanding"))
const FulfillmentLanding = lazy(() => import("src/pages/FulfillmentLanding"))
const AnalyticsLanding = lazy(() => import("src/pages/AnalyticsLanding"))
const FinanceLanding = lazy(() => import("src/pages/FinanceLanding"))
const PrintCenter = lazy(() => import("src/pages/PrintCenter"))

const PageLoader = () => (
  <Row alignmentHorizontal="center" spacingInset="500">
    <Loader size="large" />
  </Row>
)

/**
 * Root application component: auth gate, theme, error boundaries, facility
 * provider, app shell, and the lazy-loaded route table.
 */
const App: React.FC = () => {
  return (
    <Routes>
      {/* OAuth callback — no auth guard needed */}
      <Route path="/callback" element={<Suspense fallback={<PageLoader />}><Callback /></Suspense>} />

      {/* All other routes require authentication */}
      <Route path="/*" element={
        <AuthGuard>
          <Theme palette={wfmTheme}>
            <ErrorBoundary>
              <FacilityProvider>
                <AppShell>
                  <Suspense fallback={<PageLoader />}>
                    <RouteErrorBoundary>
                      <Routes>
                      <Route path="/" element={<Dashboard />} />

                      {/* Purchasing section — tabs: POs, Suppliers, Exceptions */}
                      <Route path="/po" element={<PurchasingLanding />} />
                      <Route path="/receipts" element={<PurchasingLanding />} />
                      <Route path="/suppliers" element={<PurchasingLanding />} />
                      <Route path="/invoices" element={<PurchasingLanding />} />
                      <Route path="/exceptions" element={<PurchasingLanding />} />
                      <Route path="/po/create" element={<POCreate />} />
                      <Route path="/po/:poNumber" element={<PODetail />} />
                      <Route path="/receipts/:receiptId" element={<ReceiptDetail />} />
                      <Route path="/invoices/:invoiceNumber" element={<InvoiceDetail />} />
                      <Route path="/suppliers/:code" element={<SupplierDetail />} />

                      {/* Items & Recipes section — tabs: Recipes, Ingredients */}
                      <Route path="/items/ingredients" element={<ItemsLanding />} />
                      <Route path="/items/recipes" element={<ItemsLanding />} />
                      <Route path="/recipes/:recipeCode" element={<RecipeDetail />} />
                      <Route path="/items/:itemCode" element={<ItemDetail />} />

                      {/* Inventory section — tabs: Stock, Move, Locations, Reorder, FIFO, Quality, Trace */}
                      <Route path="/inventory" element={<InventoryLanding />} />
                      <Route path="/inventory/audit" element={<InventoryLanding />} />
                      <Route path="/inventory/move" element={<InventoryLanding />} />
                      <Route path="/locations" element={<InventoryLanding />} />
                      <Route path="/reorder-alerts" element={<InventoryLanding />} />
                      <Route path="/reorder-config" element={<InventoryLanding />} />
                      <Route path="/pick-recommendation" element={<InventoryLanding />} />
                      <Route path="/quality-hold" element={<InventoryLanding />} />
                      <Route path="/trace" element={<InventoryLanding />} />

                      {/* Manufacturing section — tabs: Work Orders, Create Work Order, Multi-Level Planning, MRP */}
                      <Route path="/work-orders" element={<ManufacturingLanding />} />
                      <Route path="/work-orders/create" element={<ManufacturingLanding />} />
                      <Route path="/work-order-plans" element={<ManufacturingLanding />} />
                      <Route path="/work-orders/:workOrderId" element={<WorkOrderDetail />} />

                      {/* MRP is now a tab under Manufacturing (no separate Planning section). */}
                      {/* /production-plan kept as a back-compat alias. */}
                      <Route path="/mrp" element={<ManufacturingLanding />} />
                      <Route path="/production-plan" element={<ManufacturingLanding />} />

                      {/* Fulfillment section — tabs: Store Orders, Pick Tickets, Delivery Schedule */}
                      <Route path="/store-orders" element={<FulfillmentLanding />} />
                      <Route path="/store-orders/:orderId" element={<StoreOrderDetail />} />
                      <Route path="/pick-tickets" element={<FulfillmentLanding />} />
                      <Route path="/pick-batches/:pickBatchId" element={<PickBatchDetail />} />
                      <Route path="/delivery-schedule" element={<FulfillmentLanding />} />
                      {/* Shipping folded into Delivery Schedule (packed→ship is now
                          an action on the day board). Redirect old links. */}
                      <Route path="/shipping" element={<Navigate to="/delivery-schedule" replace />} />

                      {/* Analytics section — tabs: Supplier Metrics, Cost Summary */}
                      <Route path="/supplier-metrics" element={<AnalyticsLanding />} />
                      <Route path="/accounting" element={<AnalyticsLanding />} />

                      {/* Finance section — tabs: Overview, General Ledger, AP, AR, Periods */}
                      <Route path="/finance" element={<FinanceLanding />} />
                      <Route path="/finance/gl" element={<FinanceLanding />} />
                      <Route path="/finance/ap" element={<FinanceLanding />} />
                      <Route path="/finance/ar" element={<FinanceLanding />} />
                      <Route path="/finance/periods" element={<FinanceLanding />} />

                      {/* Central print / reprint hub */}
                      <Route path="/print-center" element={<PrintCenter />} />

                      {/* Catch-all — unknown paths render an in-shell 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </RouteErrorBoundary>
                </Suspense>
              </AppShell>
            </FacilityProvider>
          </ErrorBoundary>
        </Theme>
      </AuthGuard>
      } />
    </Routes>
  )
}

/**
 * Auth guard — keeps the user signed in, redirecting to Cognito (Midway) only
 * when there's no usable session.
 *
 * Two fixes over the naive sync check:
 *  - On a "checking" start it attempts a SILENT REFRESH (getStoredTokensAsync)
 *    before giving up, so an expired access token with a still-valid refresh
 *    token no longer bounces the user to login on navigation.
 *  - While authenticated it schedules a proactive refresh (~2 min before
 *    expiry), so the session lasts as long as the refresh token / open tab
 *    rather than the short access-token TTL.
 *
 * The redirect is a side effect, so it runs in an Effect rather than during
 * render (render must stay pure; React StrictMode double-invokes render).
 * redirectToLogin() captures the current route so re-auth returns here.
 */
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const skipAuth = import.meta.env.VITE_SKIP_AUTH === "true"
  const [status, setStatus] = useState<"checking" | "authed" | "redirecting">(
    () => (skipAuth || isAuthenticated() ? "authed" : "checking")
  )

  // Resolve a "checking" state by attempting a silent refresh before redirecting.
  useEffect(() => {
    if (status !== "checking") return
    let cancelled = false
    void getStoredTokensAsync().then((tokens) => {
      if (cancelled) return
      if (tokens) {
        setStatus("authed")
      } else {
        setStatus("redirecting")
        void redirectToLogin()
      }
    })
    return () => {
      cancelled = true
    }
  }, [status])

  // Keep the session alive while authenticated; stop the timer on unmount.
  useEffect(() => {
    if (status !== "authed" || skipAuth) return
    scheduleTokenRefresh()
    return () => stopTokenRefresh()
  }, [status, skipAuth])

  if (status !== "authed") {
    // Show a loader while we refresh-check or navigate to the hosted UI.
    return <PageLoader />
  }

  return <>{children}</>
}

/** In-shell 404 for unknown routes. */
const NotFound: React.FC = () => (
  <Column spacingInset="500" spacing="300" alignmentHorizontal="center">
    <Heading level={1} type="d50">Page not found</Heading>
    <Text type="b300" color="secondary">
      The page you&apos;re looking for doesn&apos;t exist or has moved.
    </Text>
  </Column>
)

export default App
